import * as Sentry from '@sentry/nextjs';
import type { CaptureContext, SeverityLevel } from '@sentry/types';
import type { IntlShape, MessageDescriptor } from 'react-intl';

import { getOrderAppErrorCodeMessage } from '@/errorCodes/orderApp';

import type { ProductVariant } from '@/graphql/saleor/api';

import type {
  ApiErrorWithType,
  OrderAppContext,
  ShoppingBagError,
} from './types';

const shouldSilenceError = (error: Error) => {
  return (
    error.message.startsWith(
      '[GraphQL error]: Query execution error. Link from entry',
    ) && error.message.endsWith('cannot be resolved')
  );
};

const isSentryDisabled = () =>
  typeof globalThis !== 'undefined' &&
  (
    globalThis as typeof globalThis & {
      window?: { __DISABLE_SENTRY__?: boolean };
    }
  ).window?.__DISABLE_SENTRY__ === true;

export const captureError = (error: Error, context?: CaptureContext) => {
  if (shouldSilenceError(error)) {
    return;
  }

  if (isSentryDisabled()) {
    if (process.env.NODE_ENV === 'development') console.error(error);
    return;
  }

  const errorId = Sentry.captureException(error, context);
  if (process.env.NODE_ENV === 'development') {
    console.error(error);
    return;
  }
  console.error(`Sentry error ID: ${errorId}`);
};

export const captureMessage = (
  error: string,
  context?: CaptureContext | SeverityLevel,
) => {
  if (isSentryDisabled()) {
    console.error(error);
    return;
  }
  const errorId = Sentry.captureMessage(error, context);
  console.error(`Sentry message ID: ${errorId}`);
  console.error(error);
};

export const captureMessageAndSerializeContext = (
  error: string,
  context?: CaptureContext,
) => {
  if (isSentryDisabled()) {
    console.error(error);
    return;
  }
  const errorId = Sentry.captureMessage(error, context);
  console.error(`Sentry message ID: ${errorId}`);
  if (context) {
    console.error(
      'EventPayloadJSON ' +
        JSON.stringify({
          message: error,
          ...(context as object),
        }),
    );
  } else {
    console.error(error);
  }
};

export const capture404Event = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pageName: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  payload: {
    [key: string]: string | object;
    reason: { code: string; message: string };
    requestUrl: string;
  },
) => {
  // disable temporarily until we have a better way to handle 404s
  return;
};

export const handlePromiseErrors = (
  results: PromiseSettledResult<any>[],
): Error[] => {
  const errors: Error[] = [];

  results.forEach((result) => {
    if (result.status === 'rejected') {
      errors.push(result.reason);
    } else if (Array.isArray(result.value)) {
      errors.push(
        ...result.value.filter((item) => item && item.code === 'INVALID'),
      );
    }
  });

  return errors;
};

export const parseErrors = (
  errors: ApiErrorWithType[],
  formatMessage: IntlShape['formatMessage'],
): ShoppingBagError => {
  const parsedErrors = errors
    .map((error) => {
      if (error.context) {
        try {
          const contextObj: OrderAppContext = JSON.parse(error.context);
          const skus = contextObj.skus || [contextObj.sku] || [];

          if (
            error.code === 'stock.cpi_mismatch' ||
            error.code === 'INSUFFICIENT_STOCK'
          ) {
            // Try to parse the reason field to identify problematic products
            let problematicSkus = skus;
            if (contextObj.reason) {
              const reason = contextObj.reason.toLowerCase();
              if (skus.length > 1) {
                // Look for SKUs mentioned in the reason
                const mentionedSkus = skus.filter((sku) =>
                  reason.includes(sku.toLowerCase()),
                );
                if (mentionedSkus.length > 0) {
                  problematicSkus = mentionedSkus;
                } else {
                  problematicSkus = [skus[0]];
                }
              }
            }

            return problematicSkus.reduce((acc, sku) => {
              const mappedMessage = getOrderAppErrorCodeMessage(error);
              acc[sku] = {
                code: error.code,
                message: formatMessage(mappedMessage as MessageDescriptor),
              };
              return acc;
            }, {} as ShoppingBagError);
          }

          return skus?.reduce((acc, sku) => {
            const mappedMessage = getOrderAppErrorCodeMessage(error);
            acc[sku] = {
              code: error.code,
              message: formatMessage(mappedMessage),
            };
            return acc;
          }, {} as ShoppingBagError);
        } catch (err) {
          captureMessage(`parse context error ${error.context}`);
          return null;
        }
      }
      return null;
    })
    .filter((item) => item !== null);

  return Object.assign({}, ...parsedErrors);
};

export const getErrorMessage = (
  variant: ProductVariant | undefined,
  errors?: ShoppingBagError,
) => {
  return (variant?.sku && errors?.[variant.sku]?.message) || '';
};

/**
 * Specific abort-related message patterns.
 * These are exact patterns from browser implementations:
 * - "The operation was aborted" - Chrome/Firefox AbortController
 * - "The user aborted a request" - Firefox fetch abort
 * - "signal is aborted" - AbortSignal.reason
 * - "NetworkError when attempting to fetch resource" - Firefox reload during fetch
 * - "Load failed" - Safari reload during fetch
 */
const ABORT_MESSAGE_PATTERNS = [
  'the operation was aborted',
  'the user aborted a request',
  'signal is aborted',
  'networkerror when attempting to fetch resource',
  'load failed',
] as const;

interface ErrorLike {
  cause?: unknown;
  message?: string;
  name?: string;
}

const isAbortByName = (err: ErrorLike): boolean => {
  if (err.name === 'AbortError') {
    return true;
  }
  if (err.name === 'DOMException' && err.message) {
    const msg = err.message.toLowerCase();
    return msg.includes('aborted') || msg.includes('abort');
  }
  return false;
};

const isAbortByMessage = (message: string | undefined): boolean => {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return ABORT_MESSAGE_PATTERNS.some((pattern) => normalized.includes(pattern));
};

export const isAbortLikeError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const err = error as ErrorLike;

  if (isAbortByName(err)) {
    return true;
  }

  if (isAbortByMessage(err.message)) {
    return true;
  }

  // Check nested cause (modern error chaining via Error.cause)
  if (err.cause && typeof err.cause === 'object') {
    const cause = err.cause as ErrorLike;
    if (isAbortByName(cause) || isAbortByMessage(cause.message)) {
      return true;
    }
  }

  return false;
};
